import { useState } from "react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Crown, X, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// VIP Items with their effects and how they're used in-game
export const VIP_ITEMS_CONFIG = {
  royal_scepter: {
    id: 'royal_scepter',
    name: 'Royal Scepter',
    emoji: '👑',
    rarity: 'legendary',
    description: 'Clears entire column AND row in a cross pattern',
    effect: 'cross_clear',
    useType: 'board_select', // Player selects a cell on board
    color: 'from-yellow-500 to-amber-600'
  },
  golden_crown: {
    id: 'golden_crown',
    name: 'Golden Crown',
    emoji: '🎗️',
    rarity: 'legendary',
    description: '+100% score for 3 turns',
    effect: 'score_boost',
    useType: 'instant', // Applies immediately
    duration: 3,
    color: 'from-yellow-400 to-orange-500'
  },
  diamond_heart: {
    id: 'diamond_heart',
    name: 'Diamond Heart',
    emoji: '💎',
    rarity: 'epic',
    description: 'Restores full HP instantly',
    effect: 'full_heal',
    useType: 'instant',
    color: 'from-cyan-400 to-blue-500'
  },
  ruby_ring: {
    id: 'ruby_ring',
    name: 'Ruby Ring',
    emoji: '💍',
    rarity: 'epic',
    description: '+5 extra moves',
    effect: 'extra_moves',
    useType: 'instant',
    moveBonus: 5,
    color: 'from-red-500 to-rose-600'
  },
  sapphire_orb: {
    id: 'sapphire_orb',
    name: 'Sapphire Orb',
    emoji: '🔮',
    rarity: 'epic',
    description: 'Shuffles board optimally',
    effect: 'optimal_shuffle',
    useType: 'instant',
    color: 'from-blue-500 to-indigo-600'
  },
  emerald_chalice: {
    id: 'emerald_chalice',
    name: 'Emerald Chalice',
    emoji: '🏆',
    rarity: 'epic',
    description: '2x coins for this level',
    effect: 'double_coins',
    useType: 'instant',
    color: 'from-green-500 to-emerald-600'
  },
  amethyst_amulet: {
    id: 'amethyst_amulet',
    name: 'Amethyst Amulet',
    emoji: '📿',
    rarity: 'rare',
    description: 'Highlights all matches for 30s',
    effect: 'match_highlight',
    useType: 'instant',
    duration: 30,
    color: 'from-purple-500 to-violet-600'
  },
  topaz_token: {
    id: 'topaz_token',
    name: 'Topaz Token',
    emoji: '🪙',
    rarity: 'rare',
    description: '+500 bonus coins',
    effect: 'bonus_coins',
    useType: 'instant',
    coinBonus: 500,
    color: 'from-amber-400 to-yellow-500'
  },
  pearl_pendant: {
    id: 'pearl_pendant',
    name: 'Pearl Pendant',
    emoji: '📿',
    rarity: 'rare',
    description: 'Removes all obstacles of one type',
    effect: 'obstacle_clear',
    useType: 'obstacle_select', // Player selects obstacle type
    color: 'from-slate-300 to-gray-400'
  },
  opal_orb: {
    id: 'opal_orb',
    name: 'Opal Orb',
    emoji: '🌙',
    rarity: 'rare',
    description: 'Spawns 3 random special fruits',
    effect: 'spawn_specials',
    useType: 'instant',
    specialCount: 3,
    color: 'from-pink-400 to-purple-500'
  },
  golden_dragon_scale: {
    id: 'golden_dragon_scale',
    name: 'Golden Dragon Scale',
    emoji: '🐉',
    rarity: 'legendary',
    description: '+500% damage to bosses for this fight',
    effect: 'boss_damage_boost',
    useType: 'instant',
    damageMultiplier: 6, // 500% extra = 6x total
    color: 'from-yellow-400 via-amber-500 to-orange-600'
  }
};

// Rarity colors
const RARITY_COLORS = {
  legendary: 'bg-gradient-to-r from-yellow-500 to-amber-500 text-black',
  epic: 'bg-gradient-to-r from-purple-500 to-pink-500 text-white',
  rare: 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white'
};

// VIP Items Quick Access Bar (shown during gameplay)
export function VIPItemsBar({ 
  inventory = {}, 
  onUseItem, 
  disabled = false,
  activeEffects = {} 
}) {
  const [expanded, setExpanded] = useState(false);
  
  // Get items player owns (only items with count > 0)
  const ownedItems = Object.entries(VIP_ITEMS_CONFIG)
    .filter(([id]) => {
      const count = inventory[id] || 0;
      return count > 0;
    })
    .map(([id, config]) => ({ ...config, count: inventory[id] || 0 }));
  
  // Auto-collapse panel when no items left
  if (ownedItems.length === 0) {
    return null;
  }
  
  return (
    <div className="fixed right-2 top-1/2 -translate-y-1/2 z-40">
      {/* Toggle Button */}
      <button
        onClick={() => setExpanded(!expanded)}
        className={cn(
          "w-12 h-12 rounded-full flex items-center justify-center",
          "bg-gradient-to-br from-amber-500 to-orange-600 shadow-lg",
          "border-2 border-amber-300 transition-all duration-300",
          expanded && "rotate-180"
        )}
        data-testid="vip-items-toggle"
      >
        <Crown className="w-6 h-6 text-white" />
      </button>
      
      {/* Items Panel */}
      <div className={cn(
        "absolute right-0 top-14 transition-all duration-300 origin-top-right",
        expanded ? "opacity-100 scale-100" : "opacity-0 scale-75 pointer-events-none"
      )}>
        <div className="bg-slate-900/95 backdrop-blur-md rounded-xl border border-amber-500/30 p-2 shadow-xl min-w-[180px]">
          <p className="text-xs text-amber-400 font-bold mb-2 px-2">VIP Items</p>
          <div className="space-y-1 max-h-[300px] overflow-y-auto">
            {ownedItems.map((item) => {
              const isActive = activeEffects[item.effect];
              return (
                <button
                  key={item.id}
                  onClick={() => !disabled && !isActive && onUseItem(item)}
                  disabled={disabled || isActive}
                  className={cn(
                    "w-full flex items-center gap-2 px-2 py-1.5 rounded-lg transition-all",
                    "hover:bg-white/10 active:scale-95",
                    disabled && "opacity-50 cursor-not-allowed",
                    isActive && "bg-green-500/20 border border-green-500/50"
                  )}
                  data-testid={`vip-item-${item.id}`}
                >
                  <span className="text-xl">{item.emoji}</span>
                  <div className="flex-1 text-left">
                    <p className="text-white text-xs font-medium">{item.name}</p>
                    {isActive && (
                      <p className="text-green-400 text-[10px]">Active!</p>
                    )}
                  </div>
                  <Badge className="text-[10px] px-1.5 py-0 bg-slate-700">
                    x{item.count}
                  </Badge>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

// VIP Item Use Modal (for items that need selection)
export function VIPItemUseModal({ 
  item, 
  onConfirm, 
  onCancel,
  selectMode = null, // 'board' or 'obstacle'
  obstacleTypes = []
}) {
  const [selectedObstacle, setSelectedObstacle] = useState(null);
  
  if (!item) return null;
  
  const config = VIP_ITEMS_CONFIG[item.id] || item;
  
  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div 
        className="bg-slate-900 border border-amber-500/30 rounded-2xl p-6 max-w-sm w-full"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="text-center mb-4">
          <div className={cn(
            "w-20 h-20 mx-auto rounded-2xl flex items-center justify-center text-4xl mb-3",
            "bg-gradient-to-br",
            config.color || "from-amber-500 to-orange-600"
          )}>
            {config.emoji}
          </div>
          <h3 className="text-white font-bold text-xl">{config.name}</h3>
          <Badge className={cn("mt-2", RARITY_COLORS[config.rarity])}>
            {config.rarity}
          </Badge>
        </div>
        
        {/* Description */}
        <p className="text-slate-300 text-center text-sm mb-4">
          {config.description}
        </p>
        
        {/* Selection UI for obstacle clear */}
        {selectMode === 'obstacle' && obstacleTypes.length > 0 && (
          <div className="mb-4">
            <p className="text-amber-400 text-xs font-bold mb-2">Select obstacle type to clear:</p>
            <div className="grid grid-cols-2 gap-2">
              {obstacleTypes.map((obs) => (
                <button
                  key={obs.type}
                  onClick={() => setSelectedObstacle(obs.type)}
                  className={cn(
                    "p-2 rounded-lg border transition-all text-sm",
                    selectedObstacle === obs.type
                      ? "border-amber-500 bg-amber-500/20 text-white"
                      : "border-slate-600 bg-slate-800 text-slate-300 hover:border-slate-500"
                  )}
                >
                  {obs.name} ({obs.count})
                </button>
              ))}
            </div>
          </div>
        )}
        
        {/* Board select instructions */}
        {selectMode === 'board' && (
          <div className="mb-4 p-3 bg-amber-500/10 rounded-lg border border-amber-500/30">
            <p className="text-amber-300 text-sm text-center">
              <Sparkles className="w-4 h-4 inline mr-1" />
              Tap any cell on the board to activate!
            </p>
          </div>
        )}
        
        {/* Actions */}
        <div className="flex gap-3">
          <Button
            onClick={onCancel}
            variant="outline"
            className="flex-1 border-slate-600"
          >
            Cancel
          </Button>
          <Button
            onClick={() => onConfirm(selectedObstacle)}
            disabled={selectMode === 'obstacle' && !selectedObstacle}
            className={cn(
              "flex-1 bg-gradient-to-r",
              config.color || "from-amber-500 to-orange-600"
            )}
          >
            {selectMode === 'board' ? 'Select on Board' : 'Use Item'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// VIP Item Effect Indicator (shows active effects)
export function VIPEffectIndicator({ effect, turnsLeft, timeLeft }) {
  const config = Object.values(VIP_ITEMS_CONFIG).find(c => c.effect === effect);
  if (!config) return null;
  
  return (
    <div className={cn(
      "flex items-center gap-2 px-3 py-1.5 rounded-full",
      "bg-gradient-to-r shadow-lg animate-pulse",
      config.color
    )}>
      <span className="text-lg">{config.emoji}</span>
      <span className="text-white text-sm font-bold">
        {turnsLeft !== undefined && `${turnsLeft} turns`}
        {timeLeft !== undefined && `${timeLeft}s`}
      </span>
    </div>
  );
}

// Hook to manage VIP items state in game
export function useVIPItems(userId) {
  const [inventory, setInventory] = useState({});
  const [loading, setLoading] = useState(false);
  const [activeEffects, setActiveEffects] = useState({
    score_boost: null,      // { turnsLeft: number }
    double_coins: false,    // boolean
    match_highlight: null,  // { timeLeft: number }
    boss_damage_boost: false, // +500% damage to bosses
  });
  
  // Fetch VIP items inventory
  const fetchInventory = async () => {
    if (!userId) return;
    
    try {
      const token = localStorage.getItem('fruitCrush_token');
      const response = await fetch(`${API_URL}/api/vip/items`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        const inv = {};
        data.vip_items.forEach(item => {
          inv[item.item_id] = item.owned;
        });
        setInventory(inv);
      }
    } catch (error) {
      console.error('Failed to fetch VIP items:', error);
    }
  };
  
  // Use an item (decrements from backend)
  const useItem = async (itemId) => {
    if (!userId || (inventory[itemId] || 0) <= 0) return false;
    
    setLoading(true);
    try {
      const token = localStorage.getItem('fruitCrush_token');
      const response = await fetch(`${API_URL}/api/vip/use-item`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}` 
        },
        body: JSON.stringify({ item_id: itemId })
      });
      
      if (response.ok) {
        const data = await response.json();
        setInventory(prev => ({
          ...prev,
          [itemId]: data.remaining
        }));
        return data;
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to use item');
        return false;
      }
    } catch (error) {
      console.error('Failed to use VIP item:', error);
      toast.error('Failed to use item');
      return false;
    } finally {
      setLoading(false);
    }
  };
  
  // Activate a timed effect
  const activateEffect = (effect, duration) => {
    if (effect === 'score_boost') {
      setActiveEffects(prev => ({ ...prev, score_boost: { turnsLeft: duration } }));
    } else if (effect === 'double_coins') {
      setActiveEffects(prev => ({ ...prev, double_coins: true }));
    } else if (effect === 'match_highlight') {
      setActiveEffects(prev => ({ ...prev, match_highlight: { timeLeft: duration } }));
    } else if (effect === 'boss_damage_boost') {
      setActiveEffects(prev => ({ ...prev, boss_damage_boost: true }));
    }
  };
  
  // Decrement turn-based effects
  const decrementTurnEffects = () => {
    setActiveEffects(prev => {
      const newEffects = { ...prev };
      if (newEffects.score_boost && newEffects.score_boost.turnsLeft > 0) {
        newEffects.score_boost = { turnsLeft: newEffects.score_boost.turnsLeft - 1 };
        if (newEffects.score_boost.turnsLeft <= 0) {
          newEffects.score_boost = null;
          toast.info('👑 Score boost ended');
        }
      }
      return newEffects;
    });
  };
  
  // Timer for time-based effects (match_highlight)
  const tickTimeEffects = () => {
    setActiveEffects(prev => {
      const newEffects = { ...prev };
      if (newEffects.match_highlight && newEffects.match_highlight.timeLeft > 0) {
        newEffects.match_highlight = { timeLeft: newEffects.match_highlight.timeLeft - 1 };
        if (newEffects.match_highlight.timeLeft <= 0) {
          newEffects.match_highlight = null;
          toast.info('📿 Match highlight ended');
        }
      }
      return newEffects;
    });
  };
  
  // Get score multiplier (includes score_boost effect)
  const getScoreMultiplier = () => {
    return activeEffects.score_boost ? 2 : 1; // 100% bonus = 2x
  };
  
  // Check if double coins is active
  const hasDoubleCoinBonus = () => {
    return activeEffects.double_coins;
  };
  
  // Check if match highlight is active
  const hasMatchHighlight = () => {
    return !!activeEffects.match_highlight;
  };
  
  // Get boss damage multiplier (includes boss_damage_boost effect)
  const getBossDamageMultiplier = () => {
    return activeEffects.boss_damage_boost ? 6 : 1; // 500% bonus = 6x total
  };
  
  // Reset effects (for new level)
  const resetEffects = () => {
    setActiveEffects({
      score_boost: null,
      double_coins: false,
      match_highlight: null,
      boss_damage_boost: false,
    });
  };
  
  return {
    inventory,
    loading,
    activeEffects,
    fetchInventory,
    useItem,
    activateEffect,
    decrementTurnEffects,
    tickTimeEffects,
    getScoreMultiplier,
    hasDoubleCoinBonus,
    hasMatchHighlight,
    getBossDamageMultiplier,
    resetEffects,
    setInventory
  };
}

export default VIPItemsBar;
